import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice from './RetryNotice';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, startsWithin } from './effectiveWindow';
import { RULES, numberField, firstError } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';
import useConfigAccess from './useConfigAccess';

// The departments a set can be created for: the project departments (a CRO belongs to one of them). A CRO from any other
// department still shows on the results, as "Not configured". Same list as src/warningDashboard/projectDepartments.js.
const DEPARTMENTS = [
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 23, name: 'Personal Mentor' },
  { id: 30, name: 'Open School' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => DEPARTMENTS.find((d) => String(d.id) === String(id))?.name || 'This department';

const emptyForm = {
  department_id: '',
  acl_threshold: '',
  ccr_threshold: '',
  fdr_threshold: '',
  sar_threshold: '',
  atp_threshold: '',
  is_active: true,
};

// One shared config screen for ACL/CCR/FDR/SAR/ATP - Finance sets a DEPARTMENT's five thresholds together, not on five
// separate screens (see project_threshold_config.controller.js). Those metrics are judged per CRO and a CRO is checked against
// the numbers of THEIR department (it used to be per course project). Add/edit is an inline panel on the page, not a popup.
const ProjectThresholdConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // a blank threshold only complains after the first Save attempt
  const [submitted, setSubmitted] = useState(false);

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no thresholds configured".
    return apiRequest({ url: `warning-dashboard/project-thresholds?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load the thresholds');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load the thresholds');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, department_id: defaultDepartment(DEPARTMENTS), ...emptyEffective() }); // the month to apply from is read fresh each time the form opens
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      department_id: row.department_id,
      acl_threshold: row.acl_threshold,
      ccr_threshold: row.ccr_threshold,
      fdr_threshold: row.fdr_threshold,
      sar_threshold: row.sar_threshold,
      atp_threshold: row.atp_threshold,
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  // BUG-03: ACL (days) and ATP (rupees) are never blank or negative; CCR, FDR and SAR are percentages,
  // so 0-100. One set of thresholds per department. The server enforces all of it too.
  const fieldsNow = (f) => ({
    acl: numberField(f.acl_threshold, 'ACL threshold', RULES.nonNegative10, true),
    ccr: numberField(f.ccr_threshold, 'CCR threshold', RULES.percent, true),
    fdr: numberField(f.fdr_threshold, 'FDR threshold', RULES.percent, true),
    sar: numberField(f.sar_threshold, 'SAR threshold', RULES.percent, true),
    atp: numberField(f.atp_threshold, 'ATP threshold', RULES.nonNegative10, true),
  });
  const fields = {
    acl: numberField(form.acl_threshold, 'ACL threshold', RULES.nonNegative10, submitted),
    ccr: numberField(form.ccr_threshold, 'CCR threshold', RULES.percent, submitted),
    fdr: numberField(form.fdr_threshold, 'FDR threshold', RULES.percent, submitted),
    sar: numberField(form.sar_threshold, 'SAR threshold', RULES.percent, submitted),
    atp: numberField(form.atp_threshold, 'ATP threshold', RULES.nonNegative10, submitted),
  };
  // Several versions of one threshold are normal now (a change from a later month, a one-month override), so only a
  // second ACTIVE OPEN-ENDED row starting in the chosen month or later is a conflict - one that starts earlier is simply closed when the new one begins. Edits and one-month overrides are checked by the server, which knows the months.
  const duplicateError =
    form.department_id && !editingId && rows.find((r) => r.is_active && startsWithin(r, form) && String(r.department_id) === String(form.department_id))
      ? `${departmentName(form.department_id)} already has thresholds that start in the chosen month or later - edit them, or choose a later month`
      : '';
  const departmentError = submitted && !form.department_id ? 'Choose a department' : '';

  const handleSave = () => {
    setSubmitted(true);
    if (!form.department_id || duplicateError || firstError(...Object.values(fieldsNow(form)))) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/project-thresholds/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/project-thresholds', method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save these thresholds');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save these thresholds'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r)));
    apiRequest({ url: `warning-dashboard/project-thresholds/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
        } else {
          fetchRows({ silent: true }); // a toggle can start a new version from this month
        }
      })
      .catch(() => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
      })
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  const { defaultDepartment } = useConfigAccess(); // an Add form starts on the person's own department (they can change it)

  return (
    <WarningPage
      title="ACL / CCR / FDR / SAR / ATP - Threshold Configuration"
      actions={
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button component={Link} to={`/dashboard/warning-dashboard/acl?month=${month.format('YYYY-MM-DD')}`} variant="outlined">ACL</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/ccr?month=${month.format('YYYY-MM-DD')}`} variant="outlined">CCR</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/fdr?month=${month.format('YYYY-MM-DD')}`} variant="outlined">FDR</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/sar?month=${month.format('YYYY-MM-DD')}`} variant="outlined">SAR</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/atp?month=${month.format('YYYY-MM-DD')}`} variant="outlined">ATP</Button>
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>
              Add Thresholds
            </Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each department gets one set of five thresholds, driving ACL, CCR, FDR, SAR and ATP together. These are judged per CRO:
        every CRO is checked against the numbers of their own department.
      </Typography>

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {editingId ? 'Edit Thresholds' : 'Add Thresholds'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <MetricDefinitionNote items={['acl', 'ccr', 'fdr', 'sar', 'atp']} />
            <TextField
              select
              label="Department"
              value={form.department_id}
              disabled={!!editingId}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              error={!!(departmentError || duplicateError)}
              helperText={departmentError || duplicateError || undefined}
              sx={{ maxWidth: 320 }}
            >
              {DEPARTMENTS.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>

            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="ACL warns below"
                value={form.acl_threshold}
                onChange={(e) => setForm({ ...form, acl_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...fields.acl.props}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              />
              <TextField
                label="CCR warns below"
                value={form.ccr_threshold}
                onChange={(e) => setForm({ ...form, ccr_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...fields.ccr.props}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              <TextField
                label="FDR warns above"
                value={form.fdr_threshold}
                onChange={(e) => setForm({ ...form, fdr_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...fields.fdr.props}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              <TextField
                label="SAR warns below"
                value={form.sar_threshold}
                onChange={(e) => setForm({ ...form, sar_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...fields.sar.props}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              <TextField
                label="ATP warns above"
                value={form.atp_threshold}
                onChange={(e) => setForm({ ...form, atp_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...fields.atp.props}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, no CRO of this department is flagged for these five metrics." />

            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading thresholds...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && rows.length === 0 && (
        <Typography color="text.secondary">No thresholds configured yet. Use "Add Thresholds" to set the five numbers for a department.</Typography>
      )}

      {!rowsLoading && !rowsError && rows.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell align="right"><strong>ACL warns below (days)</strong></TableCell>
                <TableCell align="right"><strong>CCR warns below (%)</strong></TableCell>
                <TableCell align="right"><strong>FDR warns above (%)</strong></TableCell>
                <TableCell align="right"><strong>SAR warns below (%)</strong></TableCell>
                <TableCell align="right"><strong>ATP warns above</strong></TableCell>
                <TableCell><strong>Applies</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{departmentName(row.department_id)}</TableCell>
                  <TableCell align="right">{row.acl_threshold}</TableCell>
                  <TableCell align="right">{row.ccr_threshold}%</TableCell>
                  <TableCell align="right">{row.fdr_threshold}%</TableCell>
                  <TableCell align="right">{row.sar_threshold}%</TableCell>
                  <TableCell align="right">₹{Number(row.atp_threshold).toLocaleString()}</TableCell>
                  <TableCell><AppliesCell row={row} month={month} /></TableCell>
                  <TableCell>
                    <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                  </TableCell>
                  <TableCell>
                    <UpdateButton size="small" action={() => openEdit(row)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default ProjectThresholdConfig;
